import * as React from "react"
import { svgIconStyles, svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgChevronRightSolid } from "@digits-shared/components/SVGIcons/solid/ChevronRightSolid.svg"
import colors from "@digits-shared/themes/colors"
import fonts, { BodyText, LabelText } from "@digits-shared/themes/typography"
import * as Accordion from "@radix-ui/react-accordion"
import styled, { css, keyframes } from "styled-components"

const ICON_STYLES = css`
  width: 24px;
  min-width: 24px;
  height: 24px;
  ${svgPathStyles(colors.secondary, 1.5)};
`

const IconContainer = styled.div`
  width: 24px;
  height: 24px;
`

const FilterChevronContainer = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${colors.translucentSecondary05};
  display: none;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease-in-out;
`

const FilterChevronIcon = styled(SvgChevronRightSolid)`
  width: 16px;
  height: 16px;
  ${svgIconStyles(colors.secondary)};
`

const AccordionRoot = styled(Accordion.Root)<{ width: number }>`
  --accordion-filter-width: ${({ width }) => width}px;
  width: var(--accordion-filter-width);
`

const AccordionTrigger = styled(Accordion.Trigger)`
  width: 100%;
  background: ${colors.transparent};
  border: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  cursor: pointer;

  &:hover {
    background: ${colors.translucentSecondary05};

    .chevronIcon {
      display: flex;
    }

    .filterIcon {
      display: none;
    }
  }
`

export const FilterAccordionItem = styled(Accordion.Item)`
  width: 100%;

  &[data-state="open"] {
    .chevronIcon {
      transform: rotate(90deg);
    }
  }

  &:not(:last-of-type) {
    border-bottom: 1px solid ${colors.secondary10};
  }

  &:last-of-type {
    &[data-state="closed"] {
      ${AccordionTrigger} {
        border-radius: 0 0 8px 8px;
      }
    }
  }
`

const AccordionTitle = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
`

const AccordionTitleText = styled(BodyText)`
  width: 100%;
  text-align: left;
  font-weight: ${fonts.weight.heavy};
`

const FilterCount = styled(LabelText)`
  border-radius: 8px;
  background: ${colors.accentBlue};
  color: ${colors.white};
  font-weight: ${fonts.weight.heavy};
  padding: 1px 0;
  width: 22px;
`

const SLIDE_DOWN = keyframes`
    from {
      opacity: 0;
      height: 0;
    }
    to {
      opacity: 1;
      height: var(--radix-accordion-content-height);
    }
`
const SLIDE_UP = keyframes`
    from {
      opacity: 1;
      height: var(--radix-accordion-content-height);
    }
    to {
      opacity: 0;
      height: 0;
    }
`

const StyledAccordionContent = styled(Accordion.Content)`
  &[data-state="open"] {
    animation: ${SLIDE_DOWN} 275ms ease-out;
  }

  &[data-state="closed"] {
    animation: ${SLIDE_UP} 275ms ease-out;
  }

  width: var(--accordion-filter-width);
  overflow: hidden;
`

/*
 * INTERFACES
 */

interface AccordionProps {
  width?: number
  defaultValue?: string
}

interface HeaderProps {
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  count: number
  label?: string
}

interface IconProps {
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  className?: string
}

/*
 * COMPONENTS
 */

export const FilterAccordion: React.FC<React.PropsWithChildren<AccordionProps>> = ({
  width = 264,
  defaultValue,
  children,
}) => (
  <AccordionRoot type="single" collapsible width={width} defaultValue={defaultValue}>
    {children}
  </AccordionRoot>
)

export const FilterAccordionHeader: React.FC<React.PropsWithChildren<HeaderProps>> = ({
  Icon,
  children,
  count,
  label,
}) => (
  <AccordionTrigger aria-label={label}>
    <AccordionTitle>
      <IconContainer>
        <IconStyled Icon={Icon} css={ICON_STYLES} className="filterIcon" />
        <FilterChevron />
      </IconContainer>
      <AccordionTitleText>{children}</AccordionTitleText>
    </AccordionTitle>

    {count > 0 && <FilterCount>{count}</FilterCount>}
  </AccordionTrigger>
)

const FilterChevron: React.FC = () => (
  <FilterChevronContainer className="chevronIcon">
    <FilterChevronIcon />
  </FilterChevronContainer>
)

const IconStyled: React.FC<IconProps> = ({ Icon, className }) => <Icon className={className} />

export const FilterAccordionContent: React.FC<React.PropsWithChildren> = ({ children }) => (
  <StyledAccordionContent asChild>
    <div>{children}</div>
  </StyledAccordionContent>
)
